import { useCallback } from 'react';

import {
  type BoostActivatedPayload,
  type BoostCombinedPayload,
  type CurrencySpentEventPayload,
  type FirebaseEvents,
  firebaseLogEvent,
} from '@/firebase';

import { type ActiveBoostByPlant } from '../types/common';
import { type BoostType, type UserFactory } from '../types/factoryType';
import { type UserQuarry } from '../types/quarryType';
import { type UserWorkshop } from '../types/workshopType';

interface UseBoostFirebaseEventsArgs {
  id?: string;
  name?: string;
  level?: number;
}

interface UseBoostFirebaseEventsReturnType {
  onBoostSuccessEvent: (
    event: FirebaseEvents,
    {
      duration,
      multiplier,
    }: Omit<BoostActivatedPayload, 'level' | 'affected_plant'>,
  ) => void;
  spendCoinEvent: ({
    currency_type,
    amount,
    reason,
  }: Omit<CurrencySpentEventPayload, 'affected_plant' | 'building_id'>) => void;
}

interface CombinedBoostType {
  plant: {
    id: string;
    name: string;
    level: number;
  };
  boostType: BoostType;
  multiplier: number;
  duration: number;
}

const useBoostFirebaseEvents = (
  { id, level, name }: UseBoostFirebaseEventsArgs,
  activeBoosts: ActiveBoostByPlant,
  plantList?: UserQuarry[] | UserFactory[] | UserWorkshop[] | null,
): UseBoostFirebaseEventsReturnType => {
  const onCombinedBoost = useCallback(
    ({ plant, boostType, multiplier, duration }: CombinedBoostType) => {
      if (plant.id in activeBoosts) {
        const activeBoostList = activeBoosts[plant.id];
        const combinedPayload = activeBoostList.reduce(
          (prevPayload: BoostCombinedPayload | undefined, activeBoost) => {
            if (boostType !== activeBoost.boostType) {
              const activeBoostIsFree = activeBoost.boostType === 'Free';
              const activeBoostIsAds = activeBoost.boostType === 'Advertising';

              if (prevPayload) {
                return {
                  ...prevPayload,
                  total_multiplier:
                    prevPayload.total_multiplier + activeBoost.multiplier,
                  boost_free_active:
                    prevPayload.boost_free_active || activeBoostIsFree,
                  boost_ads_active:
                    prevPayload.boost_ads_active || activeBoostIsAds,
                  duration: activeBoost.duration
                    ? Math.min(prevPayload.duration, activeBoost.duration)
                    : prevPayload.duration,
                };
              } else {
                return {
                  total_multiplier: multiplier + activeBoost.multiplier,
                  boost_free_active: activeBoostIsFree || boostType === 'Free',
                  boost_ads_active:
                    activeBoostIsAds || boostType === 'Advertising',
                  affected_plant: plant.name,
                  level: plant.level,
                  duration: activeBoost.duration
                    ? Math.min(duration, activeBoost.duration)
                    : duration,
                };
              }
            } else {
              return prevPayload;
            }
          },
          undefined,
        );

        if (combinedPayload) {
          console.log('boost_combined', combinedPayload);
          firebaseLogEvent('boost_combined', combinedPayload);
        }
      }
    },
    [activeBoosts],
  );

  const onBoostSuccessEvent = useCallback(
    (
      event: FirebaseEvents,
      {
        duration,
        multiplier,
      }: Omit<BoostActivatedPayload, 'level' | 'affected_plant'>,
    ) => {
      if (event === 'boost_free_activated' || event === 'boost_vip_activated') {
        if (plantList) {
          for (const plant of plantList) {
            const level = plant.level;
            const affected_plant = plant.name;
            console.log(event, { duration, multiplier, level, affected_plant });
            firebaseLogEvent(event, {
              duration,
              multiplier,
              level,
              affected_plant,
            });
            onCombinedBoost({
              plant: { id: plant.id, name: plant.name, level: plant.level },
              boostType: event === 'boost_free_activated' ? 'Free' : 'VIP',
              multiplier,
              duration,
            });
          }
        }
      } else {
        console.log(event, {
          duration,
          multiplier,
          level: level as number,
          affected_plant: name as string,
        });
        firebaseLogEvent(event, {
          duration,
          multiplier,
          level: level as number,
          affected_plant: name as string,
        });
        onCombinedBoost({
          plant: {
            id: id as string,
            name: name as string,
            level: level as number,
          },
          boostType: event === 'boost_ads_activated' ? 'Advertising' : 'Paid',
          multiplier,
          duration,
        });
      }
    },
    [plantList, level, id, name, onCombinedBoost],
  );
  const spendCoinEvent = useCallback(
    ({
      currency_type,
      amount,
      reason,
    }: Omit<CurrencySpentEventPayload, 'affected_plant' | 'building_id'>) => {
      console.log('currency_spent', {
        currency_type,
        amount,
        reason,
        affected_plant: name as string,
        building_id: id as string,
      });
      firebaseLogEvent('currency_spent', {
        currency_type,
        amount,
        reason,
        affected_plant: name as string,
        building_id: id as string,
      });
    },
    [name, id],
  );

  return { onBoostSuccessEvent, spendCoinEvent };
};

export { useBoostFirebaseEvents, type UseBoostFirebaseEventsReturnType };
